import { useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { CompanyAssetIcon, ContentIcon, DataOverviewIcon, GoodsIcon, LogIcon, PermissionIcon, UserManageIcon } from './assets'
import { useRouteNavigateHook } from '@/manager/manager/route-navigate-manager'
import { cn } from '@/lib/utils'
import { PermissionVerify } from './permission-verify'
import {
  agentPermissionHooks,
  OPS_FEATURE_CONFIG_MAP,
  PermissionKey,
} from '@/manager/manager/agent-dashboard-permission-manager'
import { useToast } from '@/hooks/use-toast'
import { WHITE_LIST_KEYS } from './constants';
import { Skeleton } from '@/components/ui/skeleton';

type SidebarGroupConfig = {
  group?: string;
  bottomLine?: boolean;
  children: unknown[];
};

const DashboardSideBarSkeleton = ({
  sidebars,
}: {
  sidebars: SidebarGroupConfig[];
}) => (
  <aside className="flex flex-col min-w-[17.57rem] py-6 px-4 border-r border-[rgba(234,236,240,1)] select-none">
    {sidebars.map((item, groupIndex) => (
      <div
        key={groupIndex}
        className={cn(
          groupIndex !== 0 && 'mt-3',
          item.bottomLine && 'border-b pb-3 border-[rgba(234,236,240,1)]'
        )}
      >
        {item.group && (
          <Skeleton className="h-4 w-28 mb-2 rounded-[4px]" />
        )}
        {item.children.map((_, childIndex) => (
          <div
            key={childIndex}
            className="flex h-9 items-center px-3 py-2 mb-1 rounded-[8px]"
          >
            <Skeleton className="w-5 h-5 shrink-0 rounded-[4px]" />
            <Skeleton className="ml-2 h-4 flex-1 max-w-[7.5rem] rounded-[4px]" />
          </div>
        ))}
      </div>
    ))}
  </aside>
);

export const DashboardSideBar = () => {
  const { t } = useTranslation();
  const { navigate } = useRouteNavigateHook.useNavigate();
  const toast = useToast();

  const params = useParams();
  const location = useLocation();

  // 刷新用户权限信息
  agentPermissionHooks.useInitialPermissionDetail();
  const { agentOpsFeatureConfigData, loading: opsFeatureConfigLoading } = agentPermissionHooks.useAgentOpsFeatureConfig();

  const handleClick = (path: string) => {
    navigate(`${path}/${params?.id}/`);
  }

  const active = (path: string) => {
    return location.pathname.includes(path)
  }

  const sidebars = useMemo(() => [
    {
      group: t('agent_dashboard.group.user_and_permission'),
      children: [
        {
          label: t('agent_dashboard.user_management.title'),
          icon: <UserManageIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <UserManageIcon className="w-5 h-5" />,
          path: '/agent/dashboard/user-management',
          permissionKey: 'user',
        },
        {
          label: t('agent_dashboard.permission.title'),
          icon: <PermissionIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <PermissionIcon className="w-5 h-5" />,
          path: '/agent/dashboard/permission',
          permissionKey: 'permission',
        },
      ]
    },
    {
      group: t('agent_dashboard.group.data_analysis'),
      children: [
        {
          label: t('agent_dashboard.data_overview.title'),
          icon: <DataOverviewIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <DataOverviewIcon className="w-5 h-5" />,
          path: '/agent/dashboard/data-overview',
          permissionKey: 'data',
        },
      ]
    },
    {
      group: t('agent_dashboard.group.content_and_order'),
      children: [
        {
          label: t('agent_dashboard.content_management.title'),
          icon: <ContentIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <ContentIcon className="w-5 h-5" />,
          path: '/agent/dashboard/content-management',
          permissionKey: 'content',
        },
        {
          label: t('agent_dashboard.order_management.title'),
          icon: <GoodsIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <GoodsIcon className="w-5 h-5" />,
          path: '/agent/dashboard/order-management',
          permissionKey: 'order',
        },
      ]
    },
    {
      group: t('agent_dashboard.group.company_asset'),
      bottomLine: true,
      children: [
        {
          label: t('agent_dashboard.company_asset.title'),
          icon: <CompanyAssetIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <CompanyAssetIcon className="w-5 h-5" />,
          path: '/agent/dashboard/company-asset',
          permissionKey: 'company',
        },
      ]
    },
    {
      children: [
        {
          label: t('agent_dashboard.operation_log.title'),
          icon: <LogIcon className="w-5 h-5 text-i-gray group-hover:text-i-black" />,
          activeIcon: <LogIcon className="w-5 h-5" />,
          path: '/agent/dashboard/operation-log',
          permissionKey: 'log',
        },
      ]
    },
  ], [t]);

  const visibleSidebars = useMemo(() => {
    const isFeatureVisible = (permissionKey: PermissionKey) =>
      agentOpsFeatureConfigData[OPS_FEATURE_CONFIG_MAP[permissionKey]];

    return sidebars
      .map((group) => ({
        ...group,
        children: group.children.filter((child) =>
          isFeatureVisible(child.permissionKey as PermissionKey)
        ),
      }))
      .filter((group) => group.children.length > 0);
  }, [sidebars, agentOpsFeatureConfigData]);

  if (opsFeatureConfigLoading) {
    return <DashboardSideBarSkeleton sidebars={sidebars} />;
  }

  return (
    <aside className='flex flex-col min-w-[17.57rem] py-6 px-4 border-r border-[rgba(234,236,240,1)] select-none'>
      {visibleSidebars.map((item, index) => (
        <div
          key={index}
          className={cn(
            index !== 0 && "mt-3",
            item.bottomLine && "border-b pb-3 border-[rgba(234,236,240,1)]"
          )}
        >
          {item.group && <div className='text-[rgba(102,112,133,1)] font-medium text-sm mb-2'>{item.group}</div>}
          {item.children.map((child, index) => (
            <PermissionVerify
              key={index}
              type="menu"
              permissionKey={child.permissionKey as PermissionKey}
              whiteList={WHITE_LIST_KEYS}
              noAuthCallback={() => {
                toast.error({
                  description: t('agent_dashboard.no_permission_tips'),
                })
              }}
            >
              <div
                key={index}
                onClick={() => handleClick(child.path)}
                className={cn(
                  "flex h-9 items-center px-3 py-2 hover:bg-[rgba(16,24,40,0.05)] mb-1 cursor-pointer transition-colors rounded-[8px]",
                  active(child.path) && "bg-[rgba(16,24,40,0.05)] text-[rgba(16,24,40,1)]",
                )}>
                <div className='mr-1'>
                  {active(child.path) ? child.activeIcon : child.icon}
                </div>
                <div className={cn(
                  "ml-2 font-medium text-sm text-[rgba(52,64,84,1)]",
                  active(child.path) && "text-[rgba(16,24,40,1)]",
                )}>
                  {child.label}
                </div>
              </div>
            </PermissionVerify>
          ))}
        </div>
      ))}
    </aside>
  )
}
